'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Input } from '@shared/components/ui/input';
import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { cn } from '@shared/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInvoicesFilters } from '../../hooks/useInvoicesFilters';

interface InvoicesFiltersPanelProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  className?: string;
}

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

/**
 * Panel de filtros del módulo Facturas (lista).
 * Número factura, rango de fechas e importe (min/max).
 */
export const InvoicesFiltersPanel = ({ initialConditions = [], onChange, className }: InvoicesFiltersPanelProps) => {
  const { t } = useTranslation('invoicesModule');

  const {
    filters,
    handleErpNumberChange,
    handleDateRangeChange,
    handleGrandTotalMinChange,
    handleGrandTotalMaxChange,
    clearAllFilters,
  } = useInvoicesFilters({ initialConditions, onChange });

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.erpNumber.trim()) count++;
    if (filters.dateRange.from && filters.dateRange.to) count++;
    if (filters.grandTotalMin.trim()) count++;
    if (filters.grandTotalMax.trim()) count++;
    return count;
  }, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FilterField label={t('table.erp_number', 'Factura')}>
          <Input
            value={filters.erpNumber}
            onChange={(e) => handleErpNumberChange(e.target.value)}
            placeholder={t('search.placeholder', 'Buscar factura...')}
            className="h-[38px]"
            data-testid="filter-erp-number"
          />
        </FilterField>

        <FilterField label={t('table.date', 'Fecha')}>
          <DateRangePicker
            initialDateFrom={filters.dateRange.from}
            initialDateTo={filters.dateRange.to}
            onUpdate={({ range }) => handleDateRangeChange(range)}
            placeholder={t('filters.date', 'Fecha')}
            dataTestId="filter-date"
            triggerClassName="h-[38px] text-sm"
          />
        </FilterField>

        <FilterField label={t('table.grand_total', 'Importe')}>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={filters.grandTotalMin}
              onChange={(e) => handleGrandTotalMinChange(e.target.value)}
              placeholder="Min"
              className="h-[38px]"
              data-testid="filter-grand-total-min"
            />
            <span className="text-muted-foreground">-</span>
            <Input
              type="number"
              value={filters.grandTotalMax}
              onChange={(e) => handleGrandTotalMaxChange(e.target.value)}
              placeholder="Max"
              className="h-[38px]"
              data-testid="filter-grand-total-max"
            />
          </div>
        </FilterField>
      </div>

      {activeFiltersCount > 0 && (
        <div className="flex justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
            data-testid="invoices-clear-filters"
          >
            {t('filters.clear_filters', 'Limpiar filtros')} ({activeFiltersCount})
          </Button>
        </div>
      )}
    </div>
  );
};
